import { JButton } from '@atoms/JButton';

import { AuthCard } from '@authentication/components/AuthCard';

import useLoginMutation from '@authentication/mutations/useLoginMutation';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import LoginRequest from '@authentication/models/login.request.dto';
import JTextFieldForm from '@atoms/JTextFieldForm';
import { ErrorLoginTypography, ForgotPWStyled } from './login-card.styled';
import packageJson from './../../../../../../package.json';
import { useNavigate } from 'react-router-dom';
import Paths from '@/routes/paths';

const initialValues = {
  username: '',
  password: '',
  provider: 'password',
};

const loginSchema = Yup.object().shape({
  username: Yup.string().email('invalid email').required('required'),
  password: Yup.string().required('required'),
  provider: Yup.string().required('required'),
});

export const LoginCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: loginMutate, isError } = useLoginMutation();

  const methods = useForm<LoginRequest>({
    mode: 'onChange',
    defaultValues: useMemo(() => initialValues, [initialValues]),
    resolver: yupResolver(loginSchema),
  });

  const { formState, handleSubmit } = methods;
  const { isDirty, isValid } = formState;

  const onSubmit = handleSubmit((data) => {
    loginMutate(data);
  });

  return (
    <AuthCard title={t('CONNECTION_TO_PROFILE')}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off">
          <Stack gap={1}>
            <JTextFieldForm
              name="username"
              data-qa="username"
              fullWidth
              noBorder
              label={t('EMAIL') ?? ''}
            />
            <JTextFieldForm
              type="password"
              name="password"
              data-qa="password"
              fullWidth
              noBorder
              label={t('PASSWORD') ?? ''}
            />
            {isError && (
              <ErrorLoginTypography data-qa="error-message">
                {t('EMAIL_OR_PASSWORD_IS_INCORRECT')}
              </ErrorLoginTypography>
            )}
            <JButton
              data-qa="login-button"
              variant="contained"
              color="primary"
              type="submit"
              guttertop
              disabled={!isValid || !isDirty}
            >
              {t('CONNECT')}
            </JButton>
          </Stack>
        </form>
      </FormProvider>

      <Stack
        alignItems={'center'}
        justifyContent={'space-between'}
        direction={'row'}
        mt={1}
      >
        <ForgotPWStyled onClick={() => navigate(Paths.ForgotPassword)} mt={2}>
          {t('FORGOT_PASSWORD_?')}
        </ForgotPWStyled>
        <Typography variant="caption">
          {t('VERSION')} {packageJson.version}
        </Typography>
      </Stack>
    </AuthCard>
  );
};
